import axios from 'axios'
import serverUrl from '@/assets/BaseServerUrl.js'

/**
 * 缓存的最佳代理节点
 * 包含：url, type, speed, name
 */
let cachedBestProxy = null;

// 从 sessionStorage 恢复缓存（如果存在）
try {
    const saved = sessionStorage.getItem('githubBestProxy');
    if (saved) cachedBestProxy = JSON.parse(saved);
} catch (e) {
    // Ignore session recovery errors
}

/**
 * 获取代理列表
 */
export async function getProxyList() {
    try {
        const res = await axios.get(`${serverUrl}/api/github-proxy/list`);
        if (res.data.success && res.data.enabled) {
            return res.data.proxies || [];
        }
        return [];
    } catch (e) {
        console.error('[GithubProxy] 获取代理列表失败:', e);
        return [];
    }
}

/**
 * 测试单个代理的速度
 * @param {Object} proxy 代理对象 {url, type, name}
 * @param {String} testFileUrl 用于测试的文件（可选，默认使用 Linux 内核 MAINTAINERS 文件）
 */
export async function testProxySpeed(proxy, testFileUrl = 'https://raw.githubusercontent.com/torvalds/linux/master/MAINTAINERS') {
    if (proxy.type === 'direct' || !proxy.url) {
        // 直连测试
        return await performTest(testFileUrl);
    }
    const proxiedUrl = applyProxy(testFileUrl, proxy);
    return await performTest(proxiedUrl);
}

async function performTest(url) {
    const startTime = Date.now();
    const controller = new AbortController();
    const TOTAL_DEADLINE = 6000; // 整体硬超时：含连接+body读取，防慢滴流节点永久卡住"测速中"
    const deadlineId = setTimeout(() => controller.abort(), TOTAL_DEADLINE);
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 连接/响应头阶段 3 秒超时

    try {
        const response = await fetch(url, {
            signal: controller.signal,
            mode: 'cors',
            cache: 'no-store'
        });
        clearTimeout(timeoutId); // 响应头已到，连接阶段结束；后续由整体 deadline 兑底

        if (!response.ok) return 0;
        if (!response.body) return 0;

        // 读取前 50KB 数据来测速；每次 read() 都受整体 deadline 兑底，
        // 慢滴流节点（能连上但几乎不出数据）会在 deadline 后被取消并判死
        const reader = response.body.getReader();
        let totalBytes = 0;
        const maxBytes = 50 * 1024;
        const MIN_BYTES = 10 * 1024; // deadline 内读不到 10KB 视为带宽过低，判死

        while (totalBytes < maxBytes) {
            const { done, value } = await reader.read();
            if (done) break;
            totalBytes += value.length;
        }
        reader.cancel().catch(() => {});

        const elapsed = (Date.now() - startTime) / 1000;
        if (totalBytes < MIN_BYTES) return 0; // 淘汰慢滴流/卡死节点
        return elapsed > 0 ? totalBytes / elapsed : 0;
    } catch (e) {
        return 0;
    } finally {
        clearTimeout(timeoutId);
        clearTimeout(deadlineId);
    }
}

/**
 * 获取最佳代理节点
 * 优先级：
 *   1. 服务端每日吞吐精测结果（speedKBps，零等待，免客户端测速）
 *   2. 兑底：直连测试 → 客户端并行测速（旧后端无测速数据时才走）
 * @param {Boolean} force 是否强制重新获取
 */
export async function getBestProxy(force = false) {
    if (!force && cachedBestProxy) return cachedBestProxy;

    // 1. 服务端每天 05:30 已对全部节点做过吞吐精测，直接复用最新结果（零等待）
    try {
        const proxies = await getProxyList();
        const serverMeasured = proxies
            .filter(p => p.speedKBps != null && p.speedKBps > 0)
            .sort((a, b) => b.speedKBps - a.speedKBps);
        if (serverMeasured.length) {
            const best = serverMeasured[0];
            const node = {
                name: best.name || best.url,
                url: best.url,
                type: best.type || 'prefix',
                status: 'done',
                speed: best.speedKBps * 1024
            };
            cachedBestProxy = node;
            sessionStorage.setItem('githubBestProxy', JSON.stringify(node));
            console.log('[GithubProxy] 使用服务端测速结果:', node.name, best.speedKBps, 'KB/s（免客户端测速）');
            return node;
        }
    } catch (e) {
        console.error('[GithubProxy] 服务端测速数据不可用，回退客户端测速:', e);
    }

    // 2. 兑底：先测试直连 (无代理) 能不能加载出来
    const directNode = { name: '直连 (无代理)', url: '', type: 'direct', status: 'waiting', speed: 0 };
    directNode.status = 'testing';
    directNode.speed = await testProxySpeed(directNode);
    directNode.status = 'done';

    // 如果直连测速大于 0，说明直连完全可行，直接返回直连
    if (directNode.speed > 0) {
        console.log('[GithubProxy] 直连可用，测试速度为:', directNode.speed, 'B/s. 优先使用直连。');
        cachedBestProxy = directNode;
        sessionStorage.setItem('githubBestProxy', JSON.stringify(directNode));
        return directNode;
    }

    console.log('[GithubProxy] 直连不可用，开始拉取并测试加速节点...');

    // 2b. 直连加载失败，开始拉取代理列表进行测试
    const proxies = await getProxyList();
    if (!proxies.length) return null;

    // 转换为测速对象格式
    const testList = proxies.map(p => ({ ...p, status: 'waiting', speed: 0 }));

    // 并行测试所有代理
    const promises = testList.map(async (p) => {
        p.status = 'testing';
        p.speed = await testProxySpeed(p);
        p.status = 'done';
    });

    await Promise.allSettled(promises);

    // 寻找最快的代理节点
    testList.sort((a, b) => b.speed - a.speed);
    const best = testList[0] && testList[0].speed > 0 ? testList[0] : null;

    if (best) {
        cachedBestProxy = best;
        sessionStorage.setItem('githubBestProxy', JSON.stringify(best));
    }

    return best;
}

/**
 * 应用代理到 URL
 */
export function applyProxy(url, proxy) {
    if (!url) return '';
    if (!proxy || proxy.type === 'direct' || !proxy.url) return url;

    if (proxy.type === 'replace') {
        const cleanProxy = proxy.url.replace(/^https?:\/\//, '').replace(/\/$/, '');
        return url
            .replace('raw.githubusercontent.com', cleanProxy)
            .replace('github.com', cleanProxy);
    } else {
        // prefix 类型
        let proxyUrl = proxy.url;
        if (!proxyUrl.endsWith('/')) proxyUrl += '/';
        // 如果 url 本身是 https:// 开头，有些前缀代理需要保留，有些不需要，通常 prefix 是直接加在前面
        return proxyUrl + url;
    }
}

/**
 * 替换 HTML 中的 GitHub 资源链接（图片、链接等）
 * @param {String} html 原始 HTML
 * @param {Object} proxy 代理节点对象
 * @param {String} baseUrl 用于补全相对路径的基础 URL (例如: https://raw.githubusercontent.com/owner/repo/HEAD/)
 */
export function proxyGithubResourcesInHtml(html, proxy, baseUrl = '') {
    if (!html || !proxy) return html;

    let processedHtml = html;

    // 1. 补全相对路径为绝对 GitHub 路径
    if (baseUrl) {
        if (!baseUrl.endsWith('/')) baseUrl += '/';
        
        // 匹配 src="..." 或 href="..."，且内容不是以 http, https, //, #, mailto, data: 开头的
        const relativeRegex = /(src|href)=["'](?!https?:\/\/|data:|\/\/|#|mailto:)([^"']+)["']/gi;
        processedHtml = processedHtml.replace(relativeRegex, (match, attr, path) => {
            // 确保 path 不以 / 开头，避免 baseUrl 拼接出双斜杠
            const cleanPath = path.replace(/^\//, '');
            const absoluteUrl = baseUrl + cleanPath;
            return `${attr}="${absoluteUrl}"`;
        });
    }

    // 2. 匹配并替换所有 GitHub 原始资源链接 (raw.githubusercontent.com 或 github.com/owner/repo/raw/...)
    // 同时也匹配已经补全后的链接
    const githubRegex = /(src|href)=["'](https?:\/\/(?:raw\.githubusercontent\.com|github\.com)[^"']+)["']/gi;
    
    return processedHtml.replace(githubRegex, (match, attr, url) => {
        const proxiedUrl = applyProxy(url, proxy);
        return `${attr}="${proxiedUrl}"`;
    });
}

/**
 * 检测 HTML 格式的 README 文档中是否已包含中文或中文链接
 * @param {String} html 已经拉取到的原始/补全后的 HTML
 * @returns {Object} { hasChinese: Boolean, reason: String, zhLink: String|null }
 */
export function detectChineseReadme(html) {
    if (!html) return { hasChinese: false, reason: 'none', zhLink: null };

    // 1. 链接引用检测：检测是否包含中文语言包的超链接
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const links = doc.querySelectorAll('a');
        for (let a of links) {
            const text = a.textContent.trim();
            const href = a.getAttribute('href');
            if (href && (
                text.includes('中文') || 
                text.includes('简体中文') || 
                text.includes('繁体中文') || 
                text.toLowerCase() === 'zh-cn' || 
                text.toLowerCase() === 'zh'
            )) {
                return { hasChinese: true, reason: 'has_zh_link', zhLink: href };
            }
        }
    } catch (e) {
        console.error('[GithubProxy] 链接检测异常:', e);
    }

    // 2. 纯中文与同文档混合内容检测：剔除代码段与标签后，统计纯文本中中文占比
    // 移除 pre, code, style, script 等标签及其内容，防止英文代码对字数比率的稀释
    let cleanText = html.replace(/<(pre|code|style|script)[^>]*>[\s\S]*?<\/\1>/gi, '');
    // 移除所有 HTML 标签，只保留纯文本
    cleanText = cleanText.replace(/<[^>]+>/g, '').replace(/\s+/g, '');

    // 匹配汉字正则 [\u4e00-\u9fa5]
    const chineseChars = cleanText.match(/[\u4e00-\u9fa5]/g) || [];
    const chineseCount = chineseChars.length;
    const totalCount = cleanText.length;

    // 设定判定阈值：
    // - 汉字总数大于 150 个（说明含有大段中文说明）
    // - 总字数大于 100 个，且中文字符占比大于 8%
    const hasChineseContent = chineseCount > 150 || (totalCount > 100 && (chineseCount / totalCount) > 0.08);

    if (hasChineseContent) {
        return { hasChinese: true, reason: 'detected_chinese', zhLink: null };
    }

    return { hasChinese: false, reason: 'none', zhLink: null };
}
